import React from "react";
import { BPEStore } from "../ProductEditorStore";
import EPDItem from "./EPDItem";
import clsx from "clsx";
import Collapse from "components/Collapse";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import ErrorMessages from "features/BuildingProductEditor/components/ErrorMessages";
import ProductChips from "../components/ProductChips";
import Checkbox from "components/Checkbox";
import { observer } from "mobx-react-lite";
import useFeatureFlag from "hooks/useFeatureFlag";

const EPDGroup = (props: { groupIndex: number }) => {
  const { t } = useTranslation();
  const group = BPEStore.data?.groups?.[props.groupIndex];
  const { isFeatureVisible } = useFeatureFlag();

  const handleOnRemoveOrReset = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    BPEStore.data.groups?.length === 1
      ? BPEStore.clearEPDGroup()
      : BPEStore.removeEPDGroup(props.groupIndex);
  };

  const addRow = () => {
    BPEStore.addEpdRow(props.groupIndex);
  };

  const handleChangeCheckBox = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    BPEStore.onChangeGenerateGraphics(props.groupIndex, e.target.checked);
  };

  const graphicEnabled = isFeatureVisible("product_graphic");

  function headerTile(): React.ReactNode {
    return (
      <div className="w-full flex justify-between items-center">
        <div className="w-full flex flex-col">
          <ProductChips
            conformity={group.conformity ? [group.conformity] : []}
            certifications={group.certifications?.map((item) => item.name)}
            healthChecked={group?.health_checked}
            containerClassName="!mt-0"
          />
          {graphicEnabled && (
            <Checkbox
              labelElement={t("productEditor.generateProductGraphicsCheckbox")}
              checked={group?.generate_graphic}
              containerClassName="!py-0 !mt-2.5"
              onChange={handleChangeCheckBox}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
        <IconTrash
          className="text-red-700 min-w-6 mr-3 cursor-pointer hover:text-red-900 transition"
          stroke={2}
          onClick={handleOnRemoveOrReset}
        />
      </div>
    );
  }

  return (
    <Collapse
      headerClassName={clsx(
        "bg-white py-3 px-4",
        graphicEnabled ? "h-[70px]" : "h-[48px]"
      )}
      containerClassName="border border-gray-200 rounded-md overflow-hidden mb-3"
      headerTitle={headerTile()}
    >
      <div className="px-4 pt-4">
        {group?.epds?.map((row, rowIndex) => (
          <EPDItem
            key={rowIndex}
            row={row}
            showLabels={rowIndex === 0}
            groupIndex={props.groupIndex}
            rowIndex={rowIndex}
          />
        ))}
        <ErrorMessages datasetIndex={props.groupIndex} className="!pt-0 pb-3" />
      </div>
      <div
        onClick={addRow}
        className={clsx(
          "flex w-full items-center justify-center cursor-pointer hover:text-indigo-500",
          "text-indigo-700 text-base font-medium py-2 border-t border-gray-200 transition"
        )}
      >
        <IconPlus className="mr-1" width={24} />
        {t("productEditor.addEpd")}
      </div>
    </Collapse>
  );
};

export default observer(EPDGroup);
